/**
 * SalesActivities.tsx — Sales / Activities feed with the Log Activity sheet
 * Design: Golden Stack — clean card list, type-coloured icons, modal for all activity types
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 * Off-scale text-12 labels stay arbitrary by the GATE 0-A map.
 */
import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import {
  Plus, Mail, Phone, Users, FileText, CheckSquare,
  MoreHorizontal, Check, User
} from 'lucide-react'
import { SectionTitle, Avatar, LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesActivity, SalesContact, SalesDeal } from '../../types'
import { useAsync, fetchActivities, fetchContacts, fetchAccount, createActivity, completeActivity, mapActivity, cachedMeIdentity, type SalesAccount360 } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { buildActivityPayload, type CreateActivityBody } from './activityLog'
import { SideSheet, SheetGroup, SheetRow, Segmented, FieldError } from './SideSheet'
import { EntityPicker } from './EntityPicker'
import { OpenDealPicker } from './OpenDealPicker'
import { autofillField } from './newDealAutofill'
import {
  contactLine, pickDeal, subjectChips,
  whenChoices, defaultWhen, resolveTaskDue, resolvedWhen, isScheduling, primaryLabel, completesAfterCreate,
  DURATIONS, outcomesFor, lastTypeKey, readLastType, writeLastType, validateLog, isSubmitChord, ownsKeyEvent,
  isoDay, type WhenChoice, type LogFieldErrors,
} from './logActivitySheet'
import { fmtCompact } from './SalesPipeline'
import { laneLabel } from './pipelineView'
import { toast } from 'sonner'
import { activityType, activityTypeControl, activityOutcomeDot } from '../../../../theme/tokens'

// One literal, two call sites (the row's tick, and the complete that follows a
// logged call / email / meeting / note) — the same condition, so one sentence.
const MARK_DONE_FAILED = 'Failed to mark activity done'

// ── Type config ───────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<SalesActivity['type'], { icon: React.ReactNode; color: string; bg: string; label: string; description: string }> = {
  email:   { icon: <Mail size={13} />,        color: activityType.email.color,   bg: activityType.email.bg,   label: 'Email',   description: 'Log an email sent or received' },
  call:    { icon: <Phone size={13} />,       color: activityType.call.color,    bg: activityType.call.bg,    label: 'Call',    description: 'Log a phone or video call' },
  meeting: { icon: <Users size={13} />,       color: activityType.meeting.color, bg: activityType.meeting.bg, label: 'Meeting', description: 'Log an in-person or virtual meeting' },
  note:    { icon: <FileText size={13} />,    color: activityType.note.color,    bg: activityType.note.bg,    label: 'Note',    description: 'Add a note about this contact or deal' },
  task:    { icon: <CheckSquare size={13} />, color: activityType.task.color,    bg: activityType.task.bg,    label: 'Task',    description: 'Create a follow-up task' },
}

// ── Log Activity sheet ────────────────────────────────────────────────────────
// Slice A (spec §3.1 as corrected by the recon; RULED 2026-10-07): the old
// modal re-framed on the New Deal sheet's SideSheet — role="dialog", the
// focus trap, the Esc rule and the discard confirm, no blur — with the
// fields in grouped lists, Contact first. A PICKED deal travels as dealId.
// Slice B (RULED 2026-10-08): the deal picker over OPEN deals with "No deal"
// always there; the type control's icons; When (Just now · Pick date ·
// Schedule…) or, for a task, Due chips with the resolved day; duration and
// outcome chips (outcome dots; hidden while scheduling); the last type used;
// inline validation, ⌘↵ and "Logging…". Pick date keeps today's backdating
// until slice D brings Earlier today / Yesterday (an occurred-at column).
// The follow-up task, stage move, summary and Undo are later slices.

// The type control, in the spec's order, each with its icon (R6: the selected
// icon's colour comes from activityTypeControl, the sheet's own token).
const TYPE_OPTIONS = (['call', 'email', 'meeting', 'note', 'task'] as const).map(t => ({
  value: t, label: TYPE_CONFIG[t].label, icon: TYPE_CONFIG[t].icon, iconColor: activityTypeControl[t],
}))

// Subject placeholders, one per type (the old modal's).
const SUBJECT_PLACEHOLDER: Record<SalesActivity['type'], string> = {
  call: 'e.g. Discovery call — discussed pricing',
  email: 'e.g. Sent proposal follow-up',
  meeting: 'e.g. Product demo with engineering team',
  note: 'e.g. Budget confirmed for Q1',
  task: 'e.g. Send case study by Friday',
}

// The field each inline error belongs to, for the scroll to the first one.
const FIELD_ID: Record<keyof LogFieldErrors, string> = {
  contact: 'log-activity-contact', subject: 'log-activity-subject', due: 'log-activity-when',
}

// The autofill tag under the deal: says WHY it is there, and goes the moment
// the rep picks a deal themselves (the New Deal sheet's AutofillTag).
function AutofillTag({ text }: { text: string | null }) {
  if (!text) return null
  return <div className="text-12 text-ink-muted mt-1.5">✦ {text}</div>
}

interface LogActivityModalProps {
  onClose: () => void
  /** Performs the write. Resolves true once the activity EXISTS on the server
   *  (the drawer then closes), false when the create was refused or failed
   *  (the drawer stays open with the rep's input intact). */
  onSave: (body: CreateActivityBody, markDone: boolean, typeLabel: string) => Promise<boolean>
  contacts: SalesContact[]
}

function LogActivityModal({ onClose, onSave, contacts }: LogActivityModalProps) {
  // The last type used, per tenant and user, keyed by the CACHED /v1/me; with
  // no identity yet the key is null — Call, and nothing is written.
  const [typeKey] = useState(() => lastTypeKey(cachedMeIdentity()))
  const [activeType, setActiveType] = useState<SalesActivity['type']>(() => readLastType(typeKey))
  // The clock, read ONCE per mount: Just now, the task chips and the date
  // bounds all hang off this one reading (the New Deal sheet's rule).
  const [now] = useState(() => new Date())
  const today = isoDay(now)
  const [when, setWhen] = useState<WhenChoice>(() => defaultWhen(readLastType(typeKey)))
  const [description, setDescription] = useState('')
  // The PICKED contact. Typing in the box clears it: a typed name is not an
  // id, and the API links a contact by id or not at all.
  const [contact, setContact] = useState<SalesContact | null>(null)
  const [contactSearch, setContactSearch] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [duration, setDuration] = useState('')
  const [outcome, setOutcome] = useState('')
  // The deal: autofilled from the contact's account, or the rep's own pick.
  const [dealId, setDealId] = useState('')
  const [dealTag, setDealTag] = useState<string | null>(null)
  const dealEdited = useRef(false)
  // The deal the rep picked, kept so its option still renders after the
  // contact (and so the account's list) changes.
  const [pickedDeal, setPickedDeal] = useState<SalesDeal | null>(null)
  const [dealSearch, setDealSearch] = useState('')
  // Inline errors show after a field's blur or a submit attempt (§3.3).
  const [attempted, setAttempted] = useState(false)
  const [blurred, setBlurred] = useState<Partial<Record<keyof LogFieldErrors, boolean>>>({})

  // Switching type keeps the contact, deal and notes; When, Duration and
  // Outcome go back to the type's defaults (spec §3.2).
  function chooseType(t: SalesActivity['type']) {
    setActiveType(t)
    setWhen(defaultWhen(t))
    setDate(''); setTime(''); setDuration(''); setOutcome('')
  }
  // Scheduling hides Outcome, so a scheduled row never carries one.
  function chooseWhen(w: WhenChoice) {
    setWhen(w)
    setDate(''); setTime('')
    if (isScheduling(activeType, w)) setOutcome('')
  }

  // One read of the picked contact's account; a failed read shows nothing.
  const accountId = contact?.accountId ?? ''
  const [acct, setAcct] = useState<{ id: string; data: SalesAccount360 } | null>(null)
  useEffect(() => {
    if (!accountId) return
    let live = true
    fetchAccount(accountId)
      .then(data => { if (live) setAcct({ id: accountId, data }) })
      .catch(() => { /* a failed read shows nothing (RULED) */ })
    return () => { live = false }
  }, [accountId])
  const detail = acct && acct.id === accountId ? acct.data : null
  const openDeals = detail?.openDeals ?? []

  // Autofill through the New Deal sheet's rule: an edited deal is never
  // touched; an empty or still-tagged one takes the account's pick.
  const latestDeal = useRef({ dealId, dealTag })
  useLayoutEffect(() => { latestDeal.current = { dealId, dealTag } })
  useEffect(() => {
    const p = detail ? pickDeal(detail.openDeals) : null
    const next = autofillField(
      { value: latestDeal.current.dealId, tag: latestDeal.current.dealTag, edited: dealEdited.current },
      p && { value: p.dealId, tag: p.tag },
    )
    if (next) { setDealId(next.value); setDealTag(next.tag) }
  }, [detail])

  const dealOptions = pickedDeal && !openDeals.some(d => d.id === pickedDeal.id) ? [...openDeals, pickedDeal] : openDeals

  function chooseDeal(id: string, deal: SalesDeal | null) {
    dealEdited.current = true
    setDealTag(null)
    setDealId(id)
    setPickedDeal(deal)
  }

  function pickContact(c: SalesContact) {
    setContact(c)
    setContactSearch(c.name)
  }
  function changeContact() {
    setContact(null)
    setContactSearch('')
    // The search box replaces the card on the next render; focus follows.
    setTimeout(() => document.getElementById('log-activity-contact')?.focus(), 0)
  }

  // "Touched" = any content field carries input. The type and the When chip
  // are excluded: each is one click to re-pick, so not worth a question.
  const latest = useRef({ description, contactSearch, notes, date, time, duration, outcome, dealId, dealSearch })
  useLayoutEffect(() => { latest.current = { description, contactSearch, notes, date, time, duration, outcome, dealId, dealSearch } })
  function touched() {
    const f = latest.current
    return f.description !== '' || f.contactSearch !== '' || f.notes !== '' || f.date !== '' ||
      f.time !== '' || f.duration !== '' || f.outcome !== '' || f.dealId !== '' || f.dealSearch !== ''
  }

  const errors = validateLog({ type: activeType, when, contactId: contact?.id ?? '', subject: description, date, today: now })
  const shownError = (f: keyof LogFieldErrors) => ((blurred[f] || attempted) ? errors[f] ?? null : null)
  // A blur counts only when focus moves to another place in the sheet, or to
  // nowhere — the New Deal sheet's rule. Focus LEAVING the dialog is the trap
  // refocusing the opener (which StrictMode also replays on mount) or the
  // sheet closing, never the rep finishing a field.
  const blurFrom = (f: keyof LogFieldErrors) => (e: React.FocusEvent) => {
    const to = e.relatedTarget
    const sheet = e.currentTarget.closest('[role="dialog"]')
    if (to instanceof Node && (e.currentTarget.contains(to) || !sheet?.contains(to))) return
    setBlurred(b => (b[f] ? b : { ...b, [f]: true }))
  }

  // No toast here: success is the parent's to announce, and only after the
  // server has answered 2xx. A refused or failed save keeps the drawer open.
  async function handleSave() {
    if (savingRef.current) return
    setAttempted(true)
    const first = (['contact', 'subject', 'due'] as const).find(f => errors[f])
    if (first) {
      document.getElementById(FIELD_ID[first])?.scrollIntoView?.({ block: 'center' })
      return
    }
    const scheduling = isScheduling(activeType, when)
    const day = activeType === 'task' ? resolveTaskDue(when, now, date) : (when === 'now' ? '' : date)
    const payload = buildActivityPayload({
      type: activeType, description, contactId: contact?.id ?? '', date: day,
      time: when === 'pick' || scheduling ? time : '',
      duration, outcome: scheduling ? '' : outcome, dealId, notes,
    })
    if (!payload.ok) return // unreachable: validateLog has already refused it
    savingRef.current = true
    setSaving(true)
    const saved = await onSave(payload.body, completesAfterCreate(activeType, when), TYPE_CONFIG[activeType].label)
    savingRef.current = false
    setSaving(false)
    if (saved) {
      writeLastType(typeKey, activeType)
      onClose()
    }
  }

  const scheduling = isScheduling(activeType, when)
  const showDuration = activeType === 'call' || activeType === 'meeting'
  const outcomes = scheduling ? [] : outcomesFor(activeType)
  const chips = subjectChips(activeType, contact)
  const whenLabel = activeType === 'task' ? 'Due' : 'When'
  const resolved = resolvedWhen({ type: activeType, when, date, time, now })
  const needsDate = when === 'pick' || scheduling

  const inputCls = "w-full px-3 py-2 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all"
  const chipCls = (active: boolean) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-12 font-medium border transition-colors ${active ? 'bg-surface-card border-ink-primary text-ink-primary' : 'bg-surface-control text-ink-secondary border-edge-control hover:border-ink-disabled'}`

  return (
    <SideSheet titleId="log-activity-title" title="Log activity"
      subtitle="Record what happened, and we'll suggest what's next."
      headerExtra={(
        <div className="mt-3">
          <Segmented label="Activity type" options={TYPE_OPTIONS} value={activeType} onChange={chooseType} />
        </div>
      )}
      footer={(
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={onClose} className="k-btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={() => void handleSave()} disabled={saving} aria-busy={saving}
            aria-keyshortcuts="Meta+Enter Control+Enter"
            className="k-btn k-btn--lg disabled:opacity-60">
            {saving ? 'Logging…' : primaryLabel(activeType, when)}
            {!saving && <span aria-hidden="true" className="ml-2 opacity-70">⌘↵</span>}
          </button>
        </div>
      )}
      onClose={onClose}
      isDirty={touched}
      discardTitle="Discard this activity?" discardBody="What you've entered will be lost."
      discardLabel="Discard activity"
      onKeyDown={e => {
        // ⌘↵ / Ctrl+↵ submits — only a key from THIS sheet, never one that
        // bubbled through the React tree from a dialog nested in it.
        if (!isSubmitChord(e) || !ownsKeyEvent(e.currentTarget, e.target)) return
        e.preventDefault()
        void handleSave()
      }}>

      <SheetGroup caption="Who and what" idBase="log-activity">
        <SheetRow label="Contact *" htmlFor="log-activity-contact">
          {contact ? (
            <div className="flex items-center gap-3">
              <Avatar name={contact.name} size={34} />
              <div className="min-w-0 flex-1">
                <div className="text-13 font-semibold text-ink-primary truncate">{contact.name}</div>
                <div className="text-12 text-ink-muted truncate">{contactLine(contact)}</div>
              </div>
              <button type="button" onClick={changeContact} className="k-btn-secondary"
                aria-label={`Change contact (${contact.name})`}>
                Change
              </button>
            </div>
          ) : (
            // The picker's own onBlur carries no event; its wrapper's does, so
            // a click on one of its own options is not a blur of the field.
            <div onBlur={blurFrom('contact')}>
              <EntityPicker<SalesContact>
                id="log-activity-contact" className={`${inputCls} pl-8`} autoFocus
                placeholder="Search by name or company..."
                text={contactSearch}
                onTextChange={setContactSearch}
                // The page's own contacts read, filtered here as the old modal
                // did — no new request.
                search={q => Promise.resolve(contacts.filter(c =>
                  c.name.toLowerCase().includes(q.toLowerCase()) ||
                  c.company.toLowerCase().includes(q.toLowerCase())).slice(0, 6))}
                rowKey={c => c.id}
                renderRow={c => (
                  <span className="flex items-center gap-3 min-w-0">
                    <Avatar name={c.name} size={22} />
                    <span className="min-w-0">
                      <span className="block text-13 font-semibold text-ink-primary truncate">{c.name}</span>
                      <span className="block text-11 text-ink-faint truncate">{c.company} · {c.title}</span>
                    </span>
                  </span>
                )}
                onPick={pickContact}
                emptyPrompt="Type a name or company"
                noMatch="No contact matches that search"
                adornment={<div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none"><User size={13} /></div>}
              />
              <FieldError id="log-activity-contact-error" text={shownError('contact')} />
            </div>
          )}
        </SheetRow>
        {/* R1: always here — "No deal", the account's open deals, and any open
            deal the rep searched for — whatever the contact or the read did. */}
        <SheetRow label="Deal" htmlFor="log-activity-deal">
          <select id="log-activity-deal" value={dealId} className={inputCls}
            onChange={e => chooseDeal(e.target.value, dealOptions.find(d => d.id === e.target.value) ?? null)}>
            <option value="">No deal</option>
            {dealOptions.map(d => (
              <option key={d.id} value={d.id}>{d.name} · {fmtCompact(d.amount, d.currency)} · {laneLabel(d.stage)}</option>
            ))}
          </select>
          <AutofillTag text={dealTag} />
          <div className="mt-2">
            <OpenDealPicker id="log-activity-deal-search" className={inputCls}
              text={dealSearch} onTextChange={setDealSearch}
              onPick={d => { chooseDeal(d.id, d); setDealSearch('') }} />
          </div>
        </SheetRow>
        <SheetRow label="Subject *" htmlFor="log-activity-subject">
          <input id="log-activity-subject" type="text" value={description} onChange={e => setDescription(e.target.value)}
            onBlur={blurFrom('subject')}
            aria-invalid={shownError('subject') !== null} aria-describedby="log-activity-subject-error"
            placeholder={SUBJECT_PLACEHOLDER[activeType]} className={inputCls} />
          <FieldError id="log-activity-subject-error" text={shownError('subject')} />
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2" aria-label="Subject suggestions">
              {chips.map(c => (
                <button key={c} type="button" onClick={() => setDescription(c)} className={chipCls(false)}>{c}</button>
              ))}
            </div>
          )}
        </SheetRow>
      </SheetGroup>

      <SheetGroup caption={activeType === 'task' ? 'Due' : 'When and how it went'} idBase="log-activity">
        <SheetRow label={whenLabel}
          aside={resolved ? <span className="text-12 text-ink-muted tabular-nums">{resolved}</span> : undefined}>
          <div id="log-activity-when" role="group" aria-label={whenLabel} className="flex flex-wrap gap-2">
            {whenChoices(activeType).map(c => (
              <button key={c.value} type="button" aria-pressed={when === c.value}
                onClick={() => chooseWhen(c.value)} className={chipCls(when === c.value)}>
                {c.label}
              </button>
            ))}
          </div>
          {needsDate && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <input id="log-activity-date" type="date" value={date} onChange={e => setDate(e.target.value)}
                onBlur={blurFrom('due')}
                aria-label={activeType === 'task' ? 'Due date' : 'Date'}
                aria-invalid={shownError('due') !== null} aria-describedby="log-activity-due-error"
                // Pick date is for the past (a future day is a schedule);
                // Schedule… starts today. The rule itself is validateLog's.
                max={activeType !== 'task' && when === 'pick' ? today : undefined}
                min={scheduling ? today : undefined}
                className={inputCls} />
              <input id="log-activity-time" type="time" value={time} onChange={e => setTime(e.target.value)}
                aria-label={activeType === 'task' ? 'Due time' : 'Time'} className={inputCls} />
            </div>
          )}
          <FieldError id="log-activity-due-error" text={shownError('due')} />
        </SheetRow>
        {showDuration && (
          <SheetRow label="Duration">
            <div role="group" aria-label="Duration" className="flex flex-wrap gap-2">
              {DURATIONS.map(d => (
                <button key={d} type="button" aria-pressed={duration === d}
                  onClick={() => setDuration(duration === d ? '' : d)} className={chipCls(duration === d)}>
                  {d}
                </button>
              ))}
            </div>
          </SheetRow>
        )}
        {outcomes.length > 0 && (
          <SheetRow label="Outcome">
            <div role="group" aria-label="Outcome" className="flex flex-wrap gap-2">
              {outcomes.map(o => (
                <button key={o.label} type="button" aria-pressed={outcome === o.label}
                  onClick={() => setOutcome(outcome === o.label ? '' : o.label)} className={chipCls(outcome === o.label)}>
                  <span aria-hidden="true" data-outcome-dot="" className="w-2 h-2 rounded-full"
                    style={{ background: activityOutcomeDot[o.tone] }} />
                  {o.label}
                </button>
              ))}
            </div>
          </SheetRow>
        )}
      </SheetGroup>

      <SheetGroup caption="Notes" idBase="log-activity">
        <textarea aria-label="Notes" value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Key points, objections, budget, timing…" rows={4}
          className="w-full px-4 py-3 bg-transparent text-13 text-ink-body placeholder:text-ink-faint resize-none rounded-tile" />
      </SheetGroup>
    </SideSheet>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

/** The contacts read's own cap — the picker's list AND the name lookup for
 *  the rows read the same page; named in the fallback note so the bound is
 *  stated (OwnerSelect's DIRECTORY_CAP shape). */
const CONTACTS_CAP = 200

export function SalesActivities() {
  const { data, loading, error } = useAsync(
    () => Promise.all([fetchActivities(), fetchContacts({ limit: CONTACTS_CAP })]),
    [],
  )
  const [activities, setActivities] = useState<SalesActivity[]>([])
  const [typeFilter, setTypeFilter] = useState<'all' | SalesActivity['type']>('all')
  const [showDone, setShowDone] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [marking, setMarking] = useState<Record<string, boolean>>({})

  // Seed local state from the fetched list (local state also holds optimistic adds).
  useEffect(() => { if (data) setActivities(data[0]) }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const contacts = data?.[1] ?? []
  // The wire names a contact by id only; the name comes from the contacts
  // read the picker already needs. An id the page does not carry renders as
  // the RAW id with a note naming the bound — never a blank, which would read
  // the same as "no contact" (the §35 ruling-3 / OwnerSelect rule).
  const contactById = new Map(contacts.map(c => [c.id, c]))

  const filtered = activities.filter(a => {
    if (typeFilter !== 'all' && a.type !== typeFilter) return false
    if (!showDone && a.done) return false
    return true
  })

  const pending = activities.filter(a => !a.done)
  const done    = activities.filter(a => a.done)

  // Both writes go to the routes Deal detail already uses. The row on screen
  // is always the SERVER's row (mapActivity of the response), never a
  // client-made one, and a success toast fires only after a confirmed 2xx.
  async function handleMarkDone(id: string) {
    if (marking[id]) return
    setMarking(prev => ({ ...prev, [id]: true }))
    try {
      const updated = await completeActivity(id)
      setActivities(prev => prev.map(a => a.id === id ? mapActivity(updated) : a))
      toast.success('Activity marked as done')
    } catch (e) {
      toast.error(apiErrorMessage(e, MARK_DONE_FAILED))
    } finally {
      setMarking(prev => { const next = { ...prev }; delete next[id]; return next })
    }
  }

  async function handleLogActivity(body: CreateActivityBody, markDone: boolean, typeLabel: string): Promise<boolean> {
    let created
    try {
      created = await createActivity(body)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to log activity'))
      return false
    }
    // The activity EXISTS from here on. If the follow-up complete fails, the
    // row is shown as the server holds it — pending — beside a real error.
    if (markDone) {
      try {
        created = await completeActivity(created.id)
      } catch (e) {
        setActivities(prev => [mapActivity(created!), ...prev])
        toast.error(apiErrorMessage(e, MARK_DONE_FAILED))
        return true
      }
    }
    setActivities(prev => [mapActivity(created!), ...prev])
    toast.success(`${typeLabel} logged successfully`)
    return true
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Activities"
        subtitle={`${pending.length} pending · ${done.length} completed today`}
        action={
          <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setShowModal(true)}>
            <Plus size={14} /> Log Activity
          </button>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'email', 'call', 'meeting', 'note', 'task'] as const).map(t => {
          const count = t === 'all' ? activities.length : activities.filter(a => a.type === t).length
          const cfg = t !== 'all' ? TYPE_CONFIG[t] : null
          return (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-2 rounded-full text-12 font-medium transition-colors flex items-center gap-2 ${typeFilter === t ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}
            >
              {cfg && <span style={{ color: cfg.color }}>{cfg.icon}</span>}
              <span className="capitalize">{t === 'all' ? 'All' : cfg!.label}</span>
              <span className={`text-overline font-mono px-2 py-1 rounded-full ${typeFilter === t ? 'bg-accent text-ink-on-accent' : 'bg-surface-panel text-ink-faint'}`}>{count}</span>
            </button>
          )
        })}
        <button
          onClick={() => setShowDone(!showDone)}
          className={`ml-auto px-3 py-2 rounded-full text-12 font-medium transition-colors border ${showDone ? 'bg-surface-control text-ink-secondary border-edge-control hover:border-edge-control-hover' : 'bg-ink-primary text-ink-on-accent border-ink-primary'}`}
        >
          {showDone ? 'Hide completed' : 'Show completed'}
        </button>
      </div>

      {/* Activity list */}
      <div className="k-card">
        <div className="divide-y divide-edge-row">
          {filtered.length === 0 ? (
            <div className="px-5 py-10 text-center text-13 text-ink-muted">No activities match your filter.</div>
          ) : (
            filtered.map(a => {
              const cfg = TYPE_CONFIG[a.type]
              const contact = a.contactId ? contactById.get(a.contactId) : undefined
              return (
                <div key={a.id} className={`flex items-start gap-4 px-5 py-4 hover:bg-surface-row-hover transition-colors ${a.done ? 'opacity-60' : ''}`}>
                  {/* Type icon */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-control flex items-center justify-center mt-1" style={{ background: cfg.bg, color: cfg.color }}>
                    {cfg.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-13 font-medium leading-snug ${a.done ? 'line-through text-ink-muted' : 'text-ink-primary'}`}>
                          {a.subject}
                        </p>
                        {a.description && (
                          <p className="text-12 text-ink-secondary mt-1 leading-snug">{a.description}</p>
                        )}
                        {/* Only what the row has. Three contact cases: no
                            contactId on the wire → no contact cell; resolved →
                            avatar + name (+ company when the record has one);
                            an id past the contacts read → the raw id and the
                            bound, no avatar (initials cut from a UUID would be
                            invented). The deal is always its raw id — the wire
                            has no title and the page holds no deals read. */}
                        {(a.contactId || a.deal) && (
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {contact && (
                              <>
                                <Avatar name={contact.name} size={18} />
                                <span className="text-12 text-ink-secondary">{contact.name}</span>
                                {contact.company && (
                                  <>
                                    <span className="text-ink-disabled">·</span>
                                    <span className="text-12 text-ink-faint">{contact.company}</span>
                                  </>
                                )}
                              </>
                            )}
                            {a.contactId && !contact && (
                              <span className="text-12 text-ink-secondary">{a.contactId} — not in the contacts read (first {CONTACTS_CAP} contacts)</span>
                            )}
                            {a.deal && (
                              <>
                                {a.contactId && <span className="text-ink-disabled">·</span>}
                                <span className="text-11 bg-surface-panel text-ink-secondary px-2 py-1 rounded-full">{a.deal}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-11 text-ink-faint">{a.time}</span>
                        <span className="text-11 text-ink-faint">by {a.owner}</span>
                        {!a.done && (
                          <button
                            onClick={() => handleMarkDone(a.id)}
                            disabled={Boolean(marking[a.id])}
                            className="w-6 h-6 rounded-full border-2 border-edge-control hover:border-accent hover:bg-surface-panel transition-colors flex items-center justify-center"
                            title="Mark as done"
                          >
                            <Check size={11} className="text-ink-faint" />
                          </button>
                        )}
                        {a.done && (
                          <div className="w-6 h-6 rounded-full bg-success-bg flex items-center justify-center">
                            <Check size={11} className="text-success" />
                          </div>
                        )}
                        <button onClick={() => toast.info('Activity options coming soon')} className="p-1 rounded hover:bg-surface-nav-hover transition-colors">
                          <MoreHorizontal size={14} className="text-ink-faint" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Log Activity Modal */}
      {showModal && (
        <LogActivityModal
          onClose={() => setShowModal(false)}
          onSave={handleLogActivity}
          contacts={contacts}
        />
      )}
    </div>
  )
}
